import { useId, type ReactNode } from 'react'
import { Input } from '../input/Input'
import { FormField } from '../form-field/FormField'
import { PhoneInput } from '../phone-input/PhoneInput'
import { DiscordLogo, Mail, MessageSquareLock, Phone } from '../icon'
import { CONTACT_METHODS, type ContactErrors, type ContactMethod, type PrimaryContact } from './contact'
import styles from './ContactInput.module.css'

export interface ContactInputProps {
  value: PrimaryContact
  onChange: (value: PrimaryContact) => void
  /** Per-field messages, e.g. from validateContact or a refused save. */
  errors?: ContactErrors
  /**
   * Values to fill in when the person picks a method — their own Discord
   * username or email, say. Only used while the value is empty or still holds
   * the previous method's suggestion, so nothing they typed is overwritten.
   */
  suggestions?: Partial<Record<ContactMethod, string>>
  /** Heading for the group. Default "Primary contact". */
  label?: string
  /** One line under the heading saying what the contact is for. */
  description?: ReactNode
  disabled?: boolean
  className?: string
}

const ICONS: Record<ContactMethod, ReactNode> = {
  discord: <DiscordLogo size={16} />,
  phone: <Phone size={16} />,
  email: <Mail size={16} />,
  // Never offered here (free text is for adults the desk types in); listed
  // only because the tables are keyed by every method.
  carousel: <MessageSquareLock size={16} />,
}

const VALUE_LABEL: Record<ContactMethod, string> = {
  discord: 'Discord username',
  phone: 'Phone number',
  email: 'Email address',
  carousel: '',
}

const PLACEHOLDER: Record<ContactMethod, string> = {
  discord: 'username',
  phone: '',
  email: 'name@example.com',
  carousel: '',
}

/**
 * Who to contact about a team, and how. The team picks the method — Carousel
 * doesn't decide that a captain's Discord is the way to reach them.
 */
export function ContactInput({
  value,
  onChange,
  errors = {},
  suggestions = {},
  label = 'Primary contact',
  description,
  disabled,
  className,
}: ContactInputProps) {
  const id = useId()
  const nameId = `${id}-name`
  const valueId = `${id}-value`
  const descId = `${id}-desc`

  const pickMethod = (method: ContactMethod) => {
    if (method === value.method) return
    const untouched = value.value === '' || value.value === suggestions[value.method]
    onChange({ ...value, method, value: untouched ? (suggestions[method] ?? '') : '' })
  }

  const classes = [styles.group, className].filter(Boolean).join(' ')

  return (
    <fieldset className={classes} disabled={disabled} aria-describedby={description ? descId : undefined}>
      <legend className={styles.legend}>{label}</legend>
      {description && <p id={descId} className={styles.description}>{description}</p>}

      <FormField label="Contact name" htmlFor={nameId} error={errors.name}>
        <Input
          id={nameId}
          value={value.name}
          onChange={e => onChange({ ...value, name: e.target.value })}
          placeholder="Who should we reach?"
          maxLength={80}
          autoComplete="name"
          error={!!errors.name}
          aria-invalid={errors.name ? true : undefined}
        />
      </FormField>

      <div className={styles.methodField}>
        <span className={styles.methodLabel} id={`${id}-method`}>Contact method</span>
        <div role="radiogroup" aria-labelledby={`${id}-method`} className={styles.methods}>
          {CONTACT_METHODS.map(m => {
            const active = m.value === value.method
            return (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={active ? styles.methodActive : styles.method}
                onClick={() => pickMethod(m.value)}
              >
                <span className={styles.methodIcon} aria-hidden="true">{ICONS[m.value]}</span>
                {m.label}
              </button>
            )
          })}
        </div>
        {errors.method && <span className={styles.error}>{errors.method}</span>}
      </div>

      <FormField label={VALUE_LABEL[value.method]} htmlFor={valueId} error={errors.value}>
        {value.method === 'phone' ? (
          <PhoneInput
            id={valueId}
            value={value.value}
            onChange={v => onChange({ ...value, value: v })}
            error={!!errors.value}
            disabled={disabled}
          />
        ) : (
          <Input
            id={valueId}
            type={value.method === 'email' ? 'email' : 'text'}
            inputMode={value.method === 'email' ? 'email' : undefined}
            autoComplete={value.method === 'email' ? 'email' : 'off'}
            autoCapitalize="none"
            spellCheck={false}
            value={value.value}
            onChange={e => onChange({ ...value, value: e.target.value })}
            placeholder={PLACEHOLDER[value.method]}
            leadIcon={value.method === 'discord' ? <span className={styles.at}>@</span> : undefined}
            error={!!errors.value}
            aria-invalid={errors.value ? true : undefined}
          />
        )}
      </FormField>
    </fieldset>
  )
}
